import React from 'react';

import { ErrorMessage } from '@eika/attention-message';
import { StandardButton } from '@eika/button';
import { IconTypes } from '@eika/icon';

import piwik from '@/piwik/piwik';

import { useNavigate } from '@domain/app';
import { GooglePayData } from '@domain/digital-wallet';
import { GpayCardStatus } from '@domain/digital-wallet/services/DigitalWalletService';
import { CreditCardTokenStatus, DigitalWalletTokenStatus } from '@generated-types';
import useText from '@hooks/use-text';

import { GooglePayAlreadyActivatedMessage } from '..';

type GooglePayActivationStatusAndRedirectProps = {
    data: GooglePayData;
    currentStatus?: GpayCardStatus;
    tokenizationError?: string;
};

interface TokenizationInvalidErrorProps {
    tokenizationError: string;
}

const TokenizationInvalidError = ({ tokenizationError }: TokenizationInvalidErrorProps) => (
    <ErrorMessage text={tokenizationError} icon={IconTypes.LUKK_24} />
);

interface EligibleStatusProps {
    data: GooglePayData;
}

const EligibleStatus = ({ data }: EligibleStatusProps) => {
    const { googleWalletGetGoing, googleWalletGetGoingCardNotEligible } = useText();
    const navigate = useNavigate();

    // For KB it was already checked
    const cardIsEligibleForGoogleWallet = data.source === 'te' ? !!data.digitalCard : true;

    if (!cardIsEligibleForGoogleWallet) {
        return <ErrorMessage text={googleWalletGetGoingCardNotEligible} icon={IconTypes.LUKK_24} />;
    }

    const navigateToGoogleWallet = () => {
        if (data.source === 'te') {
            navigate('ADD_TO_GOOGLE_WALLET')({ cid: data.card.id, ctype: data.card.type });
        } else {
            navigate('ADD_TO_GOOGLE_WALLET')({ cid: data.card.id, ctype: 'CREDIT' });
        }
    };

    return (
        <StandardButton
            data-track-id={piwik.googlePay.getGoing.dataTrackId}
            data-track-linkname={piwik.googlePay.getGoing.dataTrackLinkname}
            onClick={navigateToGoogleWallet}
        >
            {googleWalletGetGoing}
        </StandardButton>
    );
};

export const GooglePayActivationStatusAndRedirectButton = ({
    currentStatus,
    tokenizationError,
    data,
}: GooglePayActivationStatusAndRedirectProps) => {
    if (currentStatus === 'REGISTERED') {
        if (data.source === 'te' && data.digitalCard?.status === DigitalWalletTokenStatus.ACTIVE) {
            return <GooglePayAlreadyActivatedMessage />;
        }

        if (data.source === 'kb') {
            const token = data.elibigleCard.tokens.find((token) => token.status === CreditCardTokenStatus.ACTIVE);
            if (token) {
                return <GooglePayAlreadyActivatedMessage />;
            }
        }
    }

    if (tokenizationError) {
        return <TokenizationInvalidError tokenizationError={tokenizationError} />;
    }

    return <EligibleStatus data={data} />;
};
